import { Typography, Markdown } from "zudoku/components"
import { apiDescriptors } from "../../src/data/beyond-compliance/api-info";
import { ProductApis } from "../components/product/ProductApis";

export const BeyondCompliance = () => (
    <>
        <Typography className="my-5 max-w-none">
            <h1>Beyond Compliance APIs</h1>
        </Typography>
        <ProductApis { ...{ apiDescriptors }} />
        <Markdown className="py-6" content={`
## EMTECH Compliance APIs v3

The EMTECH Compliance APIs v3 provide a comprehensive suite of regulatory reporting endpoints for financial services. This version introduces enhanced batch processing capabilities, improved authentication, and streamlined reporting workflows designed specifically for upstream regulatory compliance.

## 🚀 What's New in v3

### Key Improvements

- **🔒 Enhanced Authentication**: OAuth 2.0 with scoped access control
- **📦 Batch Processing**: Submit multiple events in a single request (up to 100 items)
- **📊 Advanced Schemas**: Comprehensive data validation and typing
- **🎯 POST-Only Design**: Optimized for regulatory reporting workflows
- **📈 Better Error Handling**: Detailed validation errors with correlation IDs
- **⚡ Rate Limiting**: Built-in rate limiting with quota management
- **🔍 Audit Trails**: Enhanced compliance tracking and metadata

### Architecture Overview

\`\`\`mermaid
graph LR
    subgraph "Client App"
        A1[Reports Generator]
        A2[Batch Processor]
    end
    
    subgraph "Authentication Server"
        B1[OAuth 2.0 Token Server]
        B2[Token Validation Service]
    end
    
    subgraph "Regulatory Database"
        C1[Compliance Events Storage]
        C2[Audit Trails]
    end
    
    A1 <--> B1
    A2 <--> B2
    B2 <--> C1
    B2 <--> C2
\`\`\`

## 📚 API Services

### 1. Authentication API
- **Purpose**: OAuth 2.0 token management
- **Endpoints**: Token issuance, refresh, introspection, revocation
- **Scopes**: \`compliance:read\`, \`compliance:write\`, \`reports:submit\`, \`reports:read\`

### 2. Remittance API
- **Purpose**: Cross-border money transfer reporting
- **Features**: Transfer reporting, event tracking, risk assessment
- **Batch Support**: ✅ Up to 100 transfers per batch

### 3. Payment Service API
- **Purpose**: Payment transaction and device reporting
- **Features**: Transaction reporting, device management, consumer complaints
- **Batch Support**: ✅ Up to 100 transactions per batch

### 4. Crowdfunding API
- **Purpose**: Crowdfunding campaign and investment reporting
- **Features**: Campaign management, account reporting, funding events
- **Batch Support**: ✅ Up to 100 events per batch

### 5. Microlending API
- **Purpose**: Loan application and lifecycle reporting
- **Features**: Application reporting, status tracking, loan events
- **Batch Support**: ✅ Up to 100 applications per batch

## 🔧 Quick Start

### 1. Authentication

\`\`\`bash
# Get access token
curl -X POST https://api.qa-sandbox.emtech.com/integration/v3/auth/token \
-H "Content-Type: application/x-www-form-urlencoded" \
-H "Authorization: Basic <base64(client_id:client_secret)>" \
\`\`\`

### 2. Single Report Submission

\`\`\`bash
# Submit single remittance transfer
curl -X POST https://api.qa-sandbox.emtech.com/integration/v3/compliance/remittance/report-transfers \
-H "Authorization: Bearer <access_token>" \
-H "Content-Type: application/json" \
-d '{
    "id": "TXN-001",
    "timestamp": "2024-08-15T14:15:22Z",
    "status": {"type": "SUCCEEDED"},
    "channel": "MOBILE_APP",
    "origin": {
    "account": {"id": "SENDER-001", "type": "BANK"},
    "user": {"id": "USER-001"},
    "amount": {"value": 100000, "currency": "NGN"}
    },
    "destination": {
    "account": {"id": "RECEIVER-001", "type": "MOBILE_MONEY"},
    "user": {"id": "USER-002"},
    "amount": {"value": 25000, "currency": "GHS"}
    }
}'
\`\`\`

### 3. Batch Report Submission

\`\`\`bash
# Submit batch of remittance transfers
curl -X POST https://api.qa-sandbox.emtech.com/integration/v3/compliance/remittance/report-transfers \
-H "Authorization: Bearer <access_token>" \
-H "Content-Type: application/json" \
-d '{
    "metadata": {
    "batchId": "BATCH-20240815-001",
    "totalItems": 2,
    "submittedBy": "API-CLIENT"
    },
    "items": [
    {
        "id": "TXN-001",
        "data": {
        "id": "TXN-001",
        "timestamp": "2024-08-15T14:15:22Z",
        "status": {"type": "SUCCEEDED"}
        }
    },
    {
        "id": "TXN-002", 
        "data": {
        "id": "TXN-002",
        "timestamp": "2024-08-15T14:16:22Z",
        "status": {"type": "FAILED"}
        }
    }
    ]
}'
\`\`\`


## 🛠️ Development Tools

### Validation Script
\`\`\`bash
# Validate all API specifications
cd v3/
./validate-all.sh
\`\`\`

### Schema Bundling
\`\`\`bash
# Bundle specifications with resolved references
swagger-cli bundle remittance.yaml -o bundles/remittance-bundled.yaml
\`\`\`

### Testing
\`\`\`bash
# Run API tests (if test suite is available)
npm test
\`\`\`

## 🔍 Key Features

### Batch Processing
- **Efficiency**: Submit up to 100 reports in a single request
- **Atomicity**: All-or-nothing processing with rollback capability
- **Progress Tracking**: Real-time processing status updates
- **Error Isolation**: Individual item errors don't fail entire batch

### Enhanced Security
- **OAuth 2.0**: Industry-standard authentication
- **Scoped Access**: Fine-grained permission control
- **Token Management**: Refresh, introspection, and revocation
- **Rate Limiting**: Prevent abuse and ensure fair usage

### Compliance Features
- **Audit Trails**: Complete tracking of all submissions
- **Correlation IDs**: End-to-end request tracking
- **Metadata**: Rich contextual information
- **Risk Assessment**: Built-in risk scoring and flagging

### Developer Experience
- **OpenAPI 3.1.0**: Complete API documentation
- **Comprehensive Examples**: Real-world usage samples
- **Validation Tools**: Built-in specification validation
- **Error Details**: Detailed field-level validation errors

## 🌐 Base URLs

- **Production**: \`https://api.emtech.com/integration-live/v3\`
- **Sandbox**: \`https://api.qa-sandbox.emtech.com/integration/v3\`

## 📞 Support

- **Email**: drpsupport@emtech.com
- **Documentation**: [EMTECH Developer Portal](https://docs.emtech.com)
- **Status Page**: [EMTECH Status](https://status.emtech.com)

## 📝 License

Commercial - Contact EMTECH for licensing terms.

---

*This documentation covers EMTECH Compliance APIs v3. For previous versions, please refer to the legacy documentation.*
        `} />
    </>
)
